import type { ReactNode } from "react";
import { ResizeHandle } from "./ResizeHandle";
import { clampPanelHeight } from "@/lib/schedulePanelHeights";
import type { SchedulePanelHeight } from "@/hooks/useSchedulePanelHeight";

interface ResizablePanelSectionProps {
  panel: SchedulePanelHeight;
  /** Lowercase noun used in the handle's tooltip / screen-reader label. */
  label: string;
  /**
   * The panel body — a scroll container sized from `panel.height` by the
   * caller, since each panel scrolls differently (Radix ScrollArea needs a
   * fixed height, a plain list is happier with a max-height).
   */
  children: ReactNode;
}

/**
 * Gives a stacked schedule panel a drag handle on its bottom edge, so the
 * producer can make that panel taller or shorter (double-click resets it).
 * The bottom padding reserves the strip the handle sits in, so it never
 * overlaps the panel's own content or scrollbar.
 */
export function ResizablePanelSection({ panel, label, children }: ResizablePanelSectionProps) {
  return (
    <div className="relative pb-3">
      {children}
      <ResizeHandle
        axis="y"
        value={panel.height}
        clamp={clampPanelHeight}
        onChange={panel.setHeight}
        onReset={panel.resetHeight}
        ariaLabel={`Adjust ${label} height`}
        title={`Drag to adjust the ${label} height (double-click to reset)`}
      />
    </div>
  );
}
